import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AnalysisInsights = ({ canvasData }) => {
  const [expandedInsight, setExpandedInsight] = useState(null);

  const insights = [
    {
      id: 'strategic-recommendations',
      title: 'Strategic Recommendations',
      icon: 'Lightbulb',
      color: 'primary',
      summary: '5 key recommendations identified',
      items: [
        'Strengthen key partnerships with cloud infrastructure providers to reduce operational costs by 15-20%',
        'Expand customer segments to include enterprise clients with dedicated account management',
        'Develop mobile application to increase channel reach and customer engagement',
        'Implement tiered pricing strategy to capture different market segments effectively',
        'Invest in AI/ML capabilities to enhance value proposition and competitive differentiation'
      ]
    },
    {
      id: 'competitive-positioning',
      title: 'Competitive Positioning',
      icon: 'Target',
      color: 'success',
      summary: 'Strong position in SME segment',
      items: [
        'Market Position: Top 3 in business intelligence tools for SMEs',
        'Competitive Advantage: Integrated platform combining strategy, analytics, and goal tracking',
        'Differentiation: AI-powered insights at affordable pricing point',
        'Market Share: Growing 25% quarter-over-quarter in target segment',
        'Threat Level: Moderate - established players entering SME market'
      ]
    },
    {
      id: 'market-opportunity',
      title: 'Market Opportunity',
      icon: 'TrendingUp',
      color: 'warning',
      summary: 'High growth potential identified',
      items: [
        'Total Addressable Market: $12.5B globally for business intelligence SaaS',
        'Target Market: $3.2B in SME and startup segment',
        'Growth Rate: 18% CAGR expected over next 5 years',
        'Geographic Expansion: Opportunity in Southeast Asia and Latin America markets',
        'Vertical Expansion: High demand in healthcare, fintech, and e-commerce sectors'
      ]
    },
    {
      id: 'risk-factors',
      title: 'Risk Factor Identification',
      icon: 'AlertTriangle',
      color: 'error',
      summary: '3 critical risks require attention',
      items: [
        'Customer Concentration: Top 10 customers represent 45% of revenue - diversification needed',
        'Technology Dependency: Heavy reliance on third-party AI APIs creates vendor lock-in risk',
        'Competitive Pressure: Large enterprise players launching SME-focused products',
        'Regulatory Compliance: Data privacy regulations increasing complexity and costs',
        'Talent Retention: High demand for AI/ML engineers creating recruitment challenges'
      ]
    }
  ];

  const toggleInsight = (id) => {
    setExpandedInsight(expandedInsight === id ? null : id);
  };

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary/10', text: 'text-primary', icon: 'var(--color-primary)' },
      success: { bg: 'bg-success/10', text: 'text-success', icon: 'var(--color-success)' },
      warning: { bg: 'bg-warning/10', text: 'text-warning', icon: 'var(--color-warning)' },
      error: { bg: 'bg-error/10', text: 'text-error', icon: 'var(--color-error)' }
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Sparkles" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              AI Analysis Insights
            </h3>
            <p className="text-sm text-muted-foreground font-caption">
              Strategic recommendations based on canvas
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {insights?.map((insight) => {
            const colorClasses = getColorClasses(insight?.color);
            const isExpanded = expandedInsight === insight?.id;

            return (
              <div
                key={insight?.id}
                className="border border-border rounded-lg overflow-hidden transition-smooth"
              >
                <button
                  onClick={() => toggleInsight(insight?.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted transition-smooth"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${colorClasses?.bg} flex items-center justify-center`}>
                      <Icon name={insight?.icon} size={18} color={colorClasses?.icon} />
                    </div>
                    <div className="text-left">
                      <h4 className="text-sm font-heading font-semibold text-foreground">
                        {insight?.title}
                      </h4>
                      <p className="text-xs text-muted-foreground font-caption">
                        {insight?.summary}
                      </p>
                    </div>
                  </div>
                  <Icon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    color="var(--color-muted-foreground)"
                  />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border">
                    <ul className="space-y-2 mt-3">
                      {insight?.items?.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-xs text-foreground font-body">
                          <Icon
                            name="CheckCircle2"
                            size={14}
                            color={colorClasses?.icon}
                            className="mt-0.5 flex-shrink-0"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* SWOT Analysis Preview */}
      <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Grid2x2" size={18} color="var(--color-primary)" />
          <h4 className="text-sm font-heading font-semibold text-foreground">
            SWOT Analysis Integration
          </h4>
        </div>
        <p className="text-xs text-muted-foreground font-caption mb-3">
          Comprehensive SWOT analysis will be included in the generated report
        </p>
        <div className="grid grid-cols-2 gap-2">
          {['Strengths', 'Weaknesses', 'Opportunities', 'Threats']?.map((item) => (
            <div key={item} className="p-2 bg-muted rounded-md text-center">
              <span className="text-xs font-medium text-foreground font-body">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Financial Projection Alignment */}
      <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="DollarSign" size={18} color="var(--color-success)" />
          <h4 className="text-sm font-heading font-semibold text-foreground">
            Financial Projections
          </h4>
        </div>
        <p className="text-xs text-muted-foreground font-caption mb-3">
          Aligned with predictive analysis data
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground font-body">Revenue Forecast (12M)</span>
            <span className="font-semibold text-foreground font-heading">₹2.4M</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground font-body">Growth Rate</span>
            <span className="font-semibold text-success font-heading">+32%</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground font-body">Break-even Timeline</span>
            <span className="font-semibold text-foreground font-heading">Q3 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisInsights;